import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  UrlTree,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { ACCESS_ID_PARAM, AccessRightLevelEnum } from '@osos/shared/types';
import { Observable, of } from 'rxjs';
import { mapTo, switchMap } from 'rxjs/operators';
import { PermissionActions } from '../state/permissions/permissions.actions';

@Injectable()
export class PublicAccessPermissionGuard
  implements CanActivate, CanActivateChild
{
  constructor(private store: Store) {}

  canActivate(
    route: ActivatedRouteSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this._canAccess(route);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this._canAccess(route);
  }

  private _canAccess(
    route: ActivatedRouteSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const dataViewId = this._getDataViewIdFromRoute(route);
    //If there was no dataViewId then this guard misused
    if (!dataViewId) return of(false);

    /**
     * For now we will set the highest admin level for the current route, but once we have different requirements we need to check the following steps:
     * !Important this is for later requirements if needed
     * Check if user is not authenticated
     * If user is not authenticated give him the highest level for the current route
     * If user is authenticated check if he is System Admin/Editor/Viewer
     * if the user was not authenticated the level here should be admin
     * if the user is System view/Editor/Admin the level should be greater than None
     * Then make sure it has the highest admin level for this route
     * If the user is authenticated and has none system permissions
     * Then check the currentUserPermission if it has the current viewId
     * If the user has no system permission and the viewId doesn't belong to the user current permission then
     * Then the returned level will be none here in this case also assign the highest level
     */

    //Please note that we change the logic here we need to check the PermissionState.getActiveRouteAccessLevel
    //Where we always assign the highest access level for public context
    return this._setAdminAsHighestActiveAccess().pipe(
      switchMap(() => of(true)),
    );
  }

  private _getDataViewIdFromRoute(route: ActivatedRouteSnapshot) {
    return route?.paramMap.get(ACCESS_ID_PARAM) ?? '';
  }

  private _setAdminAsHighestActiveAccess() {
    return this.store
      .dispatch(
        new PermissionActions.SetActiveRouteAccessLevel(
          AccessRightLevelEnum.AdminAccess,
        ),
      )
      .pipe(mapTo(AccessRightLevelEnum.AdminAccess));
  }
}
